import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Node } from "@xyflow/react";
import {
  SequenceCommentZone,
  SequenceTextbox,
} from "../../models/interactions/Sequence";
import { SequenceNodeData } from "./SequenceNode";
import { useConnectNodesLeftToRight } from "./ConnectNodesLeftToRight.hook";

export type SequenceCanvasSelection = {
  nodeIds: string[];
  commentZoneIds: string[];
  textboxIds: string[];
};

export type SequenceCanvasItem =
  | { kind: "node"; id: string }
  | { kind: "commentZone"; id: string }
  | { kind: "textbox"; id: string };

export type SelectionInputMode = "replace" | "add" | "toggle";

type SelectionModifierEvent = {
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
};

type UseSequenceCanvasSelectionOptions = {
  sequenceId: string;
  nodes: Node<SequenceNodeData>[];
  commentZones: SequenceCommentZone[];
  textboxes: SequenceTextbox[];
};

const EMPTY_SELECTION: SequenceCanvasSelection = {
  nodeIds: [],
  commentZoneIds: [],
  textboxIds: [],
};

export function getSelectionInputMode(
  event: SelectionModifierEvent,
): SelectionInputMode {
  if (event.ctrlKey || event.metaKey) return "toggle";
  if (event.shiftKey) return "add";
  return "replace";
}

function itemIdsByKind(items: SequenceCanvasItem[]): SequenceCanvasSelection {
  return {
    nodeIds: items
      .filter((item) => item.kind === "node")
      .map((item) => item.id),
    commentZoneIds: items
      .filter((item) => item.kind === "commentZone")
      .map((item) => item.id),
    textboxIds: items
      .filter((item) => item.kind === "textbox")
      .map((item) => item.id),
  };
}

function orderedExistingIds(ids: string[], liveIds: string[]): string[] {
  const requested = new Set(ids);
  return liveIds.filter((id) => requested.has(id));
}

function sameSelection(
  left: SequenceCanvasSelection,
  right: SequenceCanvasSelection,
): boolean {
  return (
    left.nodeIds.length === right.nodeIds.length &&
    left.nodeIds.every((id, index) => id === right.nodeIds[index]) &&
    left.commentZoneIds.length === right.commentZoneIds.length &&
    left.commentZoneIds.every(
      (id, index) => id === right.commentZoneIds[index],
    ) &&
    left.textboxIds.length === right.textboxIds.length &&
    left.textboxIds.every((id, index) => id === right.textboxIds[index])
  );
}

export default function useSequenceCanvasSelection({
  sequenceId,
  nodes,
  commentZones,
  textboxes,
}: UseSequenceCanvasSelectionOptions) {
  const [selectionState, setSelectionState] =
    useState<SequenceCanvasSelection>(EMPTY_SELECTION);
  const previousSequenceIdRef = useRef(sequenceId);
  const { connectSelectedNodes } = useConnectNodesLeftToRight();

  const liveIds = useMemo(
    () => ({
      nodeIds: nodes.map((node) => node.id),
      commentZoneIds: commentZones.map((zone) => zone.id),
      textboxIds: textboxes.map((textbox) => textbox.id),
    }),
    [commentZones, nodes, textboxes],
  );

  const normalizeSelection = useCallback(
    (selection: SequenceCanvasSelection): SequenceCanvasSelection => ({
      nodeIds: orderedExistingIds(selection.nodeIds, liveIds.nodeIds),
      commentZoneIds: orderedExistingIds(
        selection.commentZoneIds,
        liveIds.commentZoneIds,
      ),
      textboxIds: orderedExistingIds(selection.textboxIds, liveIds.textboxIds),
    }),
    [liveIds],
  );

  const selection = useMemo(
    () => normalizeSelection(selectionState),
    [normalizeSelection, selectionState],
  );

  useEffect(() => {
    if (previousSequenceIdRef.current !== sequenceId) {
      previousSequenceIdRef.current = sequenceId;
      setSelectionState(EMPTY_SELECTION);
      return;
    }

    setSelectionState((current) => {
      const normalized = normalizeSelection(current);
      return sameSelection(current, normalized) ? current : normalized;
    });
  }, [normalizeSelection, sequenceId]);

  const setSelection = useCallback(
    (nextSelection: SequenceCanvasSelection) => {
      setSelectionState(normalizeSelection(nextSelection));
    },
    [normalizeSelection],
  );

  const replaceSelection = useCallback(
    (itemOrItems: SequenceCanvasItem | SequenceCanvasItem[]) => {
      const items = Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems];
      setSelectionState(normalizeSelection(itemIdsByKind(items)));
    },
    [normalizeSelection],
  );

  const addToSelection = useCallback(
    (itemOrItems: SequenceCanvasItem | SequenceCanvasItem[]) => {
      const items = Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems];
      const additions = itemIdsByKind(items);
      setSelectionState((current) =>
        normalizeSelection({
          nodeIds: [...current.nodeIds, ...additions.nodeIds],
          commentZoneIds: [
            ...current.commentZoneIds,
            ...additions.commentZoneIds,
          ],
          textboxIds: [...current.textboxIds, ...additions.textboxIds],
        }),
      );
    },
    [normalizeSelection],
  );

  const toggleSelection = useCallback(
    (item: SequenceCanvasItem) => {
      setSelectionState((current) => {
        const key =
          item.kind === "node"
            ? "nodeIds"
            : item.kind === "commentZone"
              ? "commentZoneIds"
              : "textboxIds";
        const ids = current[key];
        return normalizeSelection({
          ...current,
          [key]: ids.includes(item.id)
            ? ids.filter((id) => id !== item.id)
            : [...ids, item.id],
        });
      });
    },
    [normalizeSelection],
  );

  const setNodeSelection = useCallback(
    (nodeIds: string[]) => {
      setSelectionState((current) =>
        normalizeSelection({ ...current, nodeIds }),
      );
    },
    [normalizeSelection],
  );

  const clearSelection = useCallback(() => {
    setSelectionState(EMPTY_SELECTION);
  }, []);

  const selectedNodes = useMemo(() => {
    const selectedNodeIds = new Set(selection.nodeIds);
    return nodes.filter((node) => selectedNodeIds.has(node.id));
  }, [nodes, selection.nodeIds]);

  const handleConnect = useCallback(() => {
    connectSelectedNodes(selectedNodes);
  }, [connectSelectedNodes, selectedNodes]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      handleConnect();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleConnect]);

  return {
    selection,
    selectedNodes,
    replaceSelection,
    addToSelection,
    toggleSelection,
    setSelection,
    setNodeSelection,
    clearSelection,
    handleConnect,
  };
}
